import { DestroyRef, Directive, inject, signal, ViewChild, Injector, OnDestroy, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListService, LocalizationService, PagedResultDto } from '@abp/ng.core';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { ConfirmationService, Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { CrudPage } from '../crud-page/crud-page.component';
import { GenericForm } from '../generic-form/generic-form';
import { IConfirmationPopupParams } from '../interfaces/IConfirmationPopupParams';
import { catchError, concatMap, defer, EMPTY, finalize, from, map, Observable, of, Subscription, switchMap, tap, take } from 'rxjs';
import { HeaderMode } from '../table-header/enums/HeaderMode';
import { ICrudService } from '../interfaces/ICrudService';
import { FieldMapperHelper } from '../fields/field-mapper.helper';
import { QueryBuilderService } from '../services/query-builder.service';
import { FieldDefinition } from '../fields/field-definition';
import { EntityConfig } from '../base-hierarchical-crud/models/hierarchy-config';
import { localizeWithFallback } from '../utils/localization-utils';
import { ZnBulkActionResultService } from '../services/zn-bulk-action-result.service';
import { IActionBase, IActionsSchema } from '../zn-other-actions/interfaces/IAction';
import { getExcelExportErrorKey } from '../utils/excel-export-error';
import { waitForProjectedDeletion, waitForProjectedVersion } from '../utils/projection-consistency';
import type {
  ProjectionConsistencyConfig,
  ProjectionPollingOptions,
  ProjectionVersion,
} from '../utils/projection-consistency';

const DEFAULT_LOCALIZATION_RESOURCE = {
  LanguageService: 'LanguageService',
} as const;

/**
 * Base component for CRUD operations.
 * Provides common functionality for all entity components to eliminate code duplication.
 *
 * @template TDto - The entity DTO type
 * @template TCreateDto - The DTO type for creating new entities
 * @template TUpdateDto - The DTO type for updating entities
 * @template TExcelDto - The DTO type for Excel export
 */
@Directive()
export abstract class BaseCrudComponent<
  TDto extends { id?: number; concurrencyStamp?: string },
  TCreateDto,
  TUpdateDto,
  TExcelDto
> implements OnDestroy
{
  @ViewChild('crudPage') crudPage!: CrudPage;
  @ViewChild('genericForm') genericForm!: GenericForm;

  private subscription: Subscription;
  private projectionTimeoutNotified = false;
  private mutationInProgress = false;
  protected list = inject(ListService);
  protected toaster = inject(ToasterService);
  protected confirmationService = inject(ConfirmationService);
  protected localizationService = inject(LocalizationService);
  protected injector = inject(Injector);
  protected queryBuilder = inject(QueryBuilderService);
  protected bulkActionResultService = inject(ZnBulkActionResultService);
  private readonly bulkActionDestroyRef = inject(DestroyRef);

  abstract service: ICrudService<TDto, TCreateDto, TUpdateDto, TExcelDto>;
  abstract fields: FieldDefinition[];
  abstract config: EntityConfig

  exporting = signal(false);
  loading = signal(true);
  disableHeaderButtons = signal(true);
  formFields = signal<any[]>([]);
  dataset = signal<TDto[]>([]);
  selectedRows: number[] = [];
  isOpen = false;
  localizationResource = ZendraLocalizationServices ?? DEFAULT_LOCALIZATION_RESOURCE;
  rowActionConfig: { title?: string; iconClass?: string } | null = null;

  selectedEntity!: TDto;

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * Load data from backend with server-side filtering/sorting
   * This method is passed to GenericTable as backendServiceProcess
   * @param queryString - JSON string from CustomBackendService containing filters/sorting
   */
  loadData = (queryString: string): Observable<any> => {
    this.loading.set(true);

    const params = this.buildListQueryParams(queryString);
    return this.service.getList(params).pipe(
      map(response => {
        this.dataset.set((response.items || []) as TDto[]);

        return {
          data: response.items || [],
          totalCount: response.totalCount || 0,
        };
      }),
      finalize(() => {
        this.loading.set(false);
      })
    );
  };

  protected buildListQueryParams(queryString: string): Record<string, any> {
    return this.queryBuilder.buildQueryParams(queryString);
  }

  protected refreshGrid(): void {
    const gridInstance = this.crudPage?.gridTable?.grid;
    gridInstance?.extensionService?.refreshBackendDataset();
  }

  protected withGridRefreshOnActionCompleted(actions: IActionsSchema): IActionsSchema {
    const attach = (actionList: IActionBase[] = []): IActionBase[] =>
      actionList.map(action => {
        const previousOnCompleted = action.onCompleted;

        return {
          ...action,
          onCompleted: () => {
            previousOnCompleted?.();
            this.refreshGrid();
          },
        };
      });

    return {
      overTable: attach(actions.overTable),
      multipleRecord: attach(actions.multipleRecord),
      singleRecord: attach(actions.singleRecord),
    };
  }

  protected runBulkAction(
    request: (ids: number[]) => Observable<unknown>,
  ): void {
    const ids = this.selectedRows;

    if (!ids?.length) {
      return;
    }

    request(ids)
      .pipe(
        tap(result => {
          this.bulkActionResultService.handle(result as any, {
            selectedCount: ids.length,
          });
        }),
        catchError(err => {
          console.error(err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.bulkActionDestroyRef),
      )
      .subscribe();
  }

  protected clearSelection(): void {
    this.selectedRows = [];
    this.crudPage?.clearSelection?.();
  }

  create(): void {
    this.selectedEntity = {} as TDto;
    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, '', this.injector);

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::TableNewRegistry`,
      fields,
      entityLocaleKey: this.config.localizationKey,
      mode: 'create'
    });
  }

  protected waitForProjectionConsistency<TSaved>(saved: TSaved): Observable<TSaved> {
    const consistency = this.service.projectionConsistency;
    if (!consistency || !this.hasProjectionIdentity(saved)) {
      return of(saved);
    }

    return waitForProjectedVersion(
      saved,
      consistency.loadById,
      consistency.polling,
    ).pipe(
      tap(consistent => {
        if (!consistent) this.notifyProjectionTimeout(consistency);
      }),
      map(() => saved),
      takeUntilDestroyed(this.bulkActionDestroyRef),
    );
  }

  private notifyProjectionTimeout(consistency: ProjectionConsistencyConfig): void {
    if (this.projectionTimeoutNotified) return;
    this.projectionTimeoutNotified = true;
    if (consistency.onTimeout) {
      consistency.onTimeout();
    } else {
      this.toaster.warn(
        `${this.localizationResource.LanguageService}::ProjectionUpdateDelayed`,
        '',
        { life: 5000 },
      );
    }
  }

  edit(row: TDto): void {
    this.selectedEntity = { ...row };
    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, row, this.injector).map(f => ({
      ...f,
      required: f.required,
      validators: f.validators
    }));

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::EditRecord`,
      fields,
      entityLocaleKey: this.config.localizationKey,
      mode: 'edit'
    });
  }

  onRowAction(row: TDto): void {
    this.edit(row);
  }

  save(data: any): void {
    if (this.mutationInProgress) return;
    this.mutationInProgress = true;
    this.projectionTimeoutNotified = false;
    const isDuplicate = this.selectedEntity?.id && this.selectedEntity.id < 0;
    this.loading.set(true);

    if (isDuplicate && this.crudPage) {
      this.crudPage.removeDuplicatedRow(this.selectedEntity.id!);
    }

    const request =
      this.selectedEntity?.id && !isDuplicate
        ? this.service.update(
            this.selectedEntity.id,
            this.prepareUpdateDto(data, this.selectedEntity)
          )
        : this.service.create(this.prepareCreateDto(data));

    request.pipe(
      switchMap(saved => this.waitForProjectionConsistency(saved)),
      takeUntilDestroyed(this.bulkActionDestroyRef),
      finalize(() => {
        this.mutationInProgress = false;
        this.loading.set(false);
      }),
    ).subscribe({
      next: () => {
        if (!this.projectionTimeoutNotified) this.toaster.success(
          `${this.localizationResource.LanguageService}::MessageSaveSuccess`,
          '',
          { life: 5000 }
        );

        this.refreshGrid();
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  onSaveInMemory(event: { mode: HeaderMode; data: any[] }): void {
    if (this.mutationInProgress) return;
    this.mutationInProgress = true;
    this.projectionTimeoutNotified = false;
    this.loading.set(true);

    const operation$ =
      event.mode === HeaderMode.Duplication
        ? from(event.data).pipe(
            concatMap(record =>
              this.service.create(this.prepareCreateDto(record)).pipe(
                switchMap(saved => this.waitForProjectionConsistency(saved)),
              ),
            ),
          )
        : from(event.data).pipe(
            concatMap(record =>
              this.service.update(record.id, record as TUpdateDto).pipe(
                switchMap(saved => this.waitForProjectionConsistency(saved)),
              ),
            ),
          );

    this.subscription = operation$
      .pipe(
        takeUntilDestroyed(this.bulkActionDestroyRef),
        finalize(() => {
          this.mutationInProgress = false;
          this.loading.set(false);
        })
      )
      .subscribe({
        complete: () => {
          if (this.bulkActionDestroyRef.destroyed) return;
          const messageKey =
            event.mode === HeaderMode.Duplication
              ? 'MessageSaveDuplicateSuccess'
              : 'MessageSaveInlineSuccess';

          if (!this.projectionTimeoutNotified) this.toaster.success(
            `${this.localizationResource.LanguageService}::${messageKey}`,
            '',
            {
              messageLocalizationParams:
                event.mode === HeaderMode.Duplication
                  ? [event.data.length.toString()]
                  : undefined,
              life: 5000,
            }
          );

          this.crudPage?.onSaveCompleted();
          this.refreshGrid();
        }
      });
  }

  protected createProjectionConsistency<
    TInput,
    TProjection extends ProjectionVersion,
  >(
    service: {
      getList: (input: TInput) => Observable<PagedResultDto<TProjection>>;
    },
    idFilter: keyof TInput & string,
    polling?: ProjectionPollingOptions,
  ): ProjectionConsistencyConfig {
    const loadByIds = (ids: number[]) => service.getList({
      [idFilter]: ids,
      skipCount: 0,
      maxResultCount: ids.length,
    } as TInput).pipe(map(response => {
      if (!Array.isArray(response?.items)) throw new Error('Invalid projection response');
      return response.items;
    }));
    return {
      loadById: id => loadByIds([id]).pipe(map(items => items[0])),
      loadByIds,
      polling,
    };
  }

  private hasProjectionIdentity(value: unknown): value is ProjectionVersion {
    return !!value
      && typeof value === 'object'
      && typeof (value as ProjectionVersion).id === 'number';
  }

  deleteRecords(): void {
    if (this.mutationInProgress || this.selectedRows.length === 0) return;
    this.mutationInProgress = true;
    this.projectionTimeoutNotified = false;
    const selectedIds = [...this.selectedRows];
    const deleteCount = selectedIds.length;
    const options: IConfirmationPopupParams = {
      message: localizeWithFallback(this.localizationService, 
        `${this.localizationResource.LanguageService}::ConfirmationDelete`,
        deleteCount.toString()
      ),
      title: localizeWithFallback(this.localizationService, 
        `${this.localizationResource.LanguageService}::ConfirmationDeleteTitle`
      ),
      severity: 'error',
      options: {
        hideCancelBtn: false,
        yesText: `${localizeWithFallback(this.localizationService, 
          `${this.localizationResource.LanguageService}::DeleteRecord`
        )}`,
        cancelText: this.localizationResource.LanguageService + '::Cancel',
        iconTemplate: '<i class="bi bi-exclamation-octagon-fill"></i>',
      },
    };

    let commandStarted = false;
    this.subscription = defer(() => this.confirmationPopup(options)).pipe(
      take(1),
      switchMap(status => {
        if (status !== Confirmation.Status.confirm) return EMPTY;
        commandStarted = true;
        this.loading.set(true);
        return defer(() => this.service.deleteByIds(selectedIds)).pipe(
          switchMap(() => {
            const consistency = this.service.projectionConsistency;
            return consistency
              ? waitForProjectedDeletion(selectedIds, consistency).pipe(
                  tap(consistent => {
                    if (!consistent) this.notifyProjectionTimeout(consistency);
                  }),
                )
              : of(true);
          }),
        );
      }),
      takeUntilDestroyed(this.bulkActionDestroyRef),
      finalize(() => {
        this.mutationInProgress = false;
        if (commandStarted) this.loading.set(false);
      }),
    ).subscribe({
      next: () => {
        if (!this.projectionTimeoutNotified) this.toaster.success(
          `${this.localizationResource.LanguageService}::MessageDeleteSuccess`,
          '',
          { messageLocalizationParams: [deleteCount.toString()], life: 5000 },
        );
        this.clearSelection();
        this.refreshGrid();
      },
      error: () => { /* Command errors are handled by the HTTP interceptor. */ },
    });
  }

  exportToExcel(): void {
    if (this.exporting()) return;
    this.exporting.set(true);

    defer(() => this.service.getDownloadToken()).pipe(
      switchMap(token => {
        const backendService = this.crudPage?.gridTable?.gridOptions?.backendServiceApi?.service;
        const params = this.queryBuilder.buildQueryParams(backendService?.buildQuery() ?? '{}');
        const { skipCount, maxResultCount, ...filters } = params;

        return this.service.getListAsExcelFile({
          ...filters,
          downloadToken: token.token,
          fields: this.getVisibleColumns(),
        } as TExcelDto);
      }),
      tap(blob => {
        if (!(blob instanceof Blob) || /json|text\/html/i.test(blob.type)) {
          throw new Error('ExcelExportInvalidResponse');
        }
        this.downloadFile(blob, this.getEntityName());
      }),
      catchError(error => from(getExcelExportErrorKey(error)).pipe(
        tap(key => this.toaster.error(`${this.localizationResource.LanguageService}::${key}`)),
        switchMap(() => EMPTY),
      )),
      takeUntilDestroyed(this.bulkActionDestroyRef),
      finalize(() => this.exporting.set(false)),
    ).subscribe();
  }

  protected getVisibleColumns(): string {
    const gridInstance = this.crudPage?.gridTable?.grid?.slickGrid;
    if (!gridInstance) return '';

    return gridInstance.getVisibleColumns()
      .filter(
        col =>
          typeof col.name === 'string' &&
          col.name.trim() !== '' &&
          col.params?.excludeFromExport !== true
      )
      .map(col => {
        const displayNestedPath = col.params?.displayNestedPath as string | undefined;
        if (displayNestedPath && displayNestedPath.trim() !== '') {
          return displayNestedPath;
        }
        return col.field as string;
      })
      .join(',');
  }

  onRowSelection(rows: number[]): void {
    this.selectedRows = rows;
  }

  onCancel(): void {
    this.isOpen = false;
  }

  protected toggle(): void {
    this.isOpen = !this.isOpen;
  }

  protected downloadFile(data: Blob, fileName: string): void {
    const a = document.createElement('a');
    const url = window.URL.createObjectURL(data);
    a.href = url;
    a.download = fileName || 'report.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  protected confirmationPopup(options: IConfirmationPopupParams) {
    return this.confirmationService.show(
      options.message,
      options.title,
      options.severity,
      options.options
    );
  }

  protected abstract prepareUpdateDto(data: any, selected: TDto): TUpdateDto;

  protected prepareCreateDto(data: any): TCreateDto {
    return data as TCreateDto;
  }

  protected abstract getEntityName(): string;
}
